import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { 
  BookOpen, 
  Search, 
  ClipboardList, 
  MessageCircle, 
  FileText, 
  Heart,
  ArrowRight,
  ChevronDown,
  CheckCircle,
  AlertCircle,
  HelpCircle
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const guideSteps = [
  {
    icon: Search,
    title: 'Recognize the Need',
    description: 'Learn the signs that indicate your loved one may benefit from in-home care assistance.',
    tips: [
      'Difficulty with daily activities like bathing, dressing, or cooking',
      'Increasing forgetfulness or confusion',
      'Isolation or signs of loneliness',
      'Recent hospitalization or health changes',
      'Family caregiver burnout',
    ],
  },
  {
    icon: ClipboardList,
    title: 'Assess Care Needs',
    description: 'Understand the specific types and levels of care that would best support your loved one.',
    tips: [
      'Personal care needs (bathing, grooming, mobility)',
      'Household assistance (cooking, cleaning, errands)',
      'Companionship and social interaction',
      'Medical appointment transportation',
      'Medication management support',
    ],
  },
  {
    icon: MessageCircle,
    title: 'Have the Conversation',
    description: 'Tips for discussing care options with your loved one in a respectful, supportive way.',
    tips: [
      'Choose a calm, private setting',
      'Focus on their safety and well-being',
      'Involve them in decision-making',
      'Address concerns and fears openly',
      'Emphasize maintaining independence',
    ],
  },
  {
    icon: FileText,
    title: 'Choose a Provider',
    description: 'What to look for when selecting a home care agency that fits your family\'s needs.',
    tips: [
      'Verify licensing and insurance',
      'Ask about caregiver screening and training',
      'Understand pricing and service flexibility',
      'Request references from other families',
      'Ensure good communication practices',
    ],
  },
  {
    icon: Heart,
    title: 'Start Care',
    description: 'What to expect when beginning care services and how to ensure a smooth transition.',
    tips: [
      'Initial in-home assessment',
      'Personalized care plan development',
      'Caregiver introduction and matching',
      'Regular check-ins and adjustments',
      'Open communication channels',
    ],
  },
];

const faqs = [
  {
    question: 'What is non-medical home care?',
    answer: 'Non-medical home care provides assistance with daily living activities, companionship, and household tasks. Unlike medical home health care, it does not include clinical services like nursing or therapy. Heritage Home Health LLC specializes in compassionate non-medical care that helps individuals maintain independence at home.',
  },
  {
    question: 'How do I know if my loved one needs home care?',
    answer: 'Signs that your loved one may benefit from home care include difficulty with daily tasks (bathing, dressing, cooking), forgetting medications, increased isolation, recent falls or health changes, or if family caregivers are experiencing burnout. A free consultation can help assess specific needs.',
  },
  {
    question: 'How are caregivers selected and trained?',
    answer: 'At Heritage Home Health LLC, all caregivers undergo thorough background checks, reference verification, and skills assessments. They receive comprehensive training in care techniques, safety protocols, and communication skills, with ongoing education to maintain the highest standards.',
  },
  {
    question: 'Can care schedules be adjusted?',
    answer: 'Absolutely. We understand that needs change over time. Heritage Home Health LLC offers flexible scheduling from a few hours a week to 24/7 care, and we can adjust services as circumstances evolve. There are no long-term contracts required.',
  },
  {
    question: 'How much does home care cost?',
    answer: 'Costs vary based on the type and amount of care needed. Heritage Home Health LLC offers a free consultation to assess needs and provide transparent pricing. Many families find in-home care more affordable than assisted living facilities.',
  },
  {
    question: 'Does insurance cover home care services?',
    answer: 'Coverage varies by policy. Long-term care insurance often covers non-medical home care. Some veterans may qualify for VA benefits. Medicare typically covers medical home health but not non-medical care. We can help you understand your options.',
  },
  {
    question: 'What if we need to change caregivers?',
    answer: 'Your comfort and satisfaction are our priorities. If for any reason the caregiver-client match isn\'t working well, Heritage Home Health LLC will work with you to find a better fit. Open communication helps us ensure the best care experience.',
  },
  {
    question: 'How do you ensure quality care?',
    answer: 'Heritage Home Health LLC maintains quality through careful caregiver selection, ongoing training, regular supervision, and consistent communication with families. We conduct regular check-ins and welcome feedback to continuously improve our services.',
  },
];

const warningSignsList = [
  'Unexplained weight loss or poor nutrition',
  'Declining personal hygiene',
  'Unpaid bills or financial disorganization',
  'Neglected home maintenance',
  'Withdrawal from social activities',
  'Confusion about medications',
  'Increased accidents or falls',
  'Mood changes or depression signs',
];

export const HomeCareGuidePage = () => {
  return (
    <>
      <Helmet>
        <title>Home Care Guide | Heritage Home Health LLC</title>
        <meta
          name="description"
          content="A comprehensive guide to understanding home care services, recognizing when care is needed, and choosing the right provider. Expert guidance from Heritage Home Health LLC."
        />
      </Helmet>
      <div className="min-h-screen bg-warm-beige" data-testid="home-care-guide-page">
        <Navigation />
        <main>
          {/* Hero Section */}
          <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-deep-blue/5 via-transparent to-transparent" />
            <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-soft-gold/15 to-transparent rounded-full blur-3xl" />
            
            <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="w-16 h-16 rounded-2xl bg-soft-gold/20 flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="w-8 h-8 text-soft-gold" />
                </div>
                <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
                  Resource Center
                </span>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy mb-6">
                  Home Care Guide by{' '}
                  <span className="text-gradient-gold">Heritage Home Health LLC</span>
                </h1>
                <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  Everything you need to know about in-home care—from recognizing when it's needed 
                  to finding the right caregiver. Your comprehensive resource for making informed care decisions.
                </p>
              </motion.div>
            </div>
          </section>

          {/* Steps Guide */}
          <section className="py-16 md:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
                  Getting Started
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-navy mb-6">
                  Your Path to <span className="text-gradient-gold">Quality Care</span>
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  Follow these steps to navigate the process of arranging in-home care 
                  for your loved one with confidence.
                </p>
              </motion.div>

              <div className="space-y-8">
                {guideSteps.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <motion.div
                      key={step.title}
                      initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="glass-strong shadow-soft-3d rounded-3xl p-8 md:p-10"
                      data-testid={`guide-step-${index}`}
                    >
                      <div className="flex flex-col md:flex-row gap-8">
                        <div className="flex-shrink-0">
                          <div className="flex items-center space-x-4">
                            <div className="w-14 h-14 rounded-2xl bg-soft-gold/20 flex items-center justify-center">
                              <Icon className="w-7 h-7 text-soft-gold" />
                            </div>
                            <div className="w-10 h-10 rounded-full bg-navy flex items-center justify-center text-white font-bold">
                              {index + 1}
                            </div>
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-serif text-2xl text-navy mb-3">{step.title}</h3>
                          <p className="text-gray-600 mb-6">{step.description}</p>
                          <ul className="grid md:grid-cols-2 gap-3">
                            {step.tips.map((tip, i) => (
                              <li key={i} className="flex items-start space-x-2">
                                <CheckCircle className="w-5 h-5 text-muted-green flex-shrink-0 mt-0.5" />
                                <span className="text-gray-700 text-sm">{tip}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Warning Signs */}
          <section className="py-16 md:py-24 bg-warm-beige">
            <div className="max-w-5xl mx-auto px-6 md:px-12">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <AlertCircle className="w-8 h-8 text-soft-gold" />
                    <h2 className="font-serif text-3xl text-navy">Signs to Watch For</h2>
                  </div>
                  <p className="text-lg text-gray-600 mb-8">
                    These warning signs may indicate that your loved one could benefit 
                    from additional support at home. Early intervention often leads to 
                    better outcomes.
                  </p>
                  <Link
                    to="/contact"
                    className="group inline-flex items-center space-x-2 bg-navy text-white px-6 py-3 rounded-full font-semibold hover:bg-deep-blue transition-all shadow-soft-3d hover:shadow-soft-3d-lg hover:-translate-y-1"
                  >
                    <span>Get a Free Assessment</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-3xl shadow-soft-3d p-8"
                >
                  <ul className="space-y-4">
                    {warningSignsList.map((sign, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-center space-x-3"
                      >
                        <div className="w-2 h-2 rounded-full bg-soft-gold" />
                        <span className="text-gray-700">{sign}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            </div>
          </section>

          {/* FAQs */}
          <section id="faqs" className="py-16 md:py-24 bg-white scroll-mt-32">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <div className="w-14 h-14 rounded-2xl bg-soft-gold/20 flex items-center justify-center mx-auto mb-6">
                  <HelpCircle className="w-7 h-7 text-soft-gold" />
                </div>
                <h2 className="font-serif text-4xl md:text-5xl text-navy mb-6">
                  Frequently Asked <span className="text-gradient-gold">Questions</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Find answers to common questions about home care services.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
                  {faqs.map((faq, index) => (
                    <AccordionItem
                      key={index}
                      value={`item-${index}`}
                      className="bg-warm-beige rounded-2xl border-none overflow-hidden"
                    >
                      <AccordionTrigger className="px-6 py-5 text-left font-semibold text-navy hover:text-soft-gold hover:no-underline">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6 text-gray-600 leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            </div>
          </section>

          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default HomeCareGuidePage;
